import type {
	SearchSelectionEvent,
	SelectAddressEvent
} from '~/composables/useNewSearchSuggest';
import type { FiltersAppliedClick } from '~/types/mixpanel';
import type { Ref } from 'vue';

import { until, useEventBus } from '@vueuse/core';
import { useTrackSearchPropertyEvents } from '~/composables/events/useTrackSearchPropertyEvents';
import { waitForUrlSync } from '~/composables/useRouteQueryHelpers';
import { MixpanelProps } from '~/core/service/mixpanel';
import { toArray } from '~~/utils';
import { nextTick, onUnmounted, ref } from 'vue';

const FILTER_TYPE_MAP = {
	macroregion: 'region',
	city: 'city',
	region: 'neighborhood'
} as const;

const NAME_KEY_MAP = {
	macroregion: MixpanelProps.REGION_NAME,
	city: MixpanelProps.CITY_NAME,
	region: MixpanelProps.NEIGHBORHOODS_NAME
} as const;

interface UseShortsFiltersSearchOptions {
	countProperties?: () => number;
	isFetchingProperties?: () => boolean;
	skipTracking?: boolean;
	onLocationSelected?: () => void;
}

/**
 * Search bar integration: dedicated event bus, maps selections onto the passed
 * location refs (from `useShortsFiltersStore` in Bar), tracks Mixpanel.
 * For URL-backed filter state alone, use the store.
 */
export function useShortsFiltersSearch(
	cities: Ref<string[]>,
	regions: Ref<string[]>,
	macroregions: Ref<string[]>,
	states: Ref<string[]>,
	options: UseShortsFiltersSearchOptions = {}
) {
	const {
		countProperties = () => 0,
		isFetchingProperties = () => false,
		skipTracking = false,
		onLocationSelected
	} = options;

	const { trackFiltersAppliedResumed } = useTrackSearchPropertyEvents();

	const shortsSearchSuggestBus = useEventBus<SearchSelectionEvent>(
		Symbol('shortsSearchSuggestSelection')
	);

	const internalSearchTerm = ref('');
	const previousSearchTerm = ref('');

	const route = useRoute();

	async function handleSearchSuggestSelectAddress(event: SelectAddressEvent) {
		const {
			searchType,
			macroregionUsed,
			cities: eventCities,
			regions: eventRegions,
			macroregions: eventMacroregions,
			states: eventStates,
			title,
			textSearched
		} = event;

		const resolvedCities =
			searchType === 'macroregion' ? [] : toArray(eventCities);
		const resolvedRegions =
			searchType === 'macroregion' ? [] : toArray(eventRegions);
		const resolvedMacroregions =
			searchType === 'macroregion' && macroregionUsed
				? [macroregionUsed]
				: toArray(eventMacroregions);
		const resolvedStates = toArray(eventStates);

		cities.value = resolvedCities;
		regions.value = resolvedRegions;
		macroregions.value = resolvedMacroregions;
		states.value = resolvedStates;

		nextTick(() => onLocationSelected?.());

		const filterApplied =
			searchType && searchType in FILTER_TYPE_MAP
				? FILTER_TYPE_MAP[searchType as keyof typeof FILTER_TYPE_MAP]
				: 'neighborhood';

		const nameEntry =
			searchType && searchType in NAME_KEY_MAP
				? {
						[NAME_KEY_MAP[searchType as keyof typeof NAME_KEY_MAP]]: title || ''
					}
				: {};

		const actualTextSearched = textSearched ?? internalSearchTerm.value;

		if (skipTracking) return;

		await waitForUrlSync(route);
		await until(isFetchingProperties).toBeTruthy({
			timeout: 500,
			throwOnTimeout: false
		});
		await until(() => !isFetchingProperties()).toBeTruthy();

		trackFiltersAppliedResumed({
			[MixpanelProps.FILTERS_APPLIED]: filterApplied,
			[MixpanelProps.CURRENT_PAGE]: 'Shorts Home',
			[MixpanelProps.TOTAL_RESULTS]: countProperties(),
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
			[MixpanelProps.TEXT_SEARCHED]: actualTextSearched,
			...nameEntry
		} as FiltersAppliedClick);
	}

	const unsubscribe = onClientEventBus(
		shortsSearchSuggestBus,
		(event: SearchSelectionEvent) => {
			if (event.type === 'address' || event.type === 'place') {
				handleSearchSuggestSelectAddress(event.payload);
			}
		}
	);

	onUnmounted(() => {
		unsubscribe();
	});

	function handleSearchTermUpdate(val: string) {
		previousSearchTerm.value = internalSearchTerm.value;
		internalSearchTerm.value = val;
	}

	return {
		shortsSearchSuggestBus,
		internalSearchTerm,
		handleSearchTermUpdate
	};
}
